/**
 * CartItem Component - Steve Jobs Redesign
 *
 * Product as hero. Simplicity. Focus.
 * - Large, beautiful product thumbnail
 * - Clean product information
 * - Minimal, elegant controls
 *
 * @file CartItem.tsx
 * @version 3.0.0 - Steve Jobs redesign
 */

import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Minus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useRemoveCartItem, useUpdateCartItem } from '../hooks/useCart';
import { useCloseCartDrawer } from '../hooks/useCartStore';
import { formatPrice } from '@/shared/utils';
import type { CartLineItem } from '../api/cart.api';
import { Button, Box, AbsoluteCenter, Center, Icon, LiquidGlass, Row, Stack, Text } from '@/ui';
import { buildProductViewUrl } from '@/features/workshop/utils/workshop-url';

// Motion Box component for animations with UI layer compliance
const MotionBox = motion(Box);

// Module-level animation constants
const itemAnimation = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10, height: 0 },
  transition: { duration: 0.2 },
} as const;

const thumbnailHover = { scale: 1.02 } as const;
const buttonHover = { scale: 1.05 } as const;
const buttonTap = { scale: 0.95 } as const;

interface CartItemProps {
  item: CartLineItem;
}

export const CartItem = React.memo(function CartItem({ item }: CartItemProps) {
  const { t } = useTranslation();
  const { mutate: updateQuantity, isPending: isUpdating } = useUpdateCartItem();
  const { mutate: removeItem, isPending: isRemoving } = useRemoveCartItem();
  const closeDrawer = useCloseCartDrawer();
  const [isHovered, setIsHovered] = useState(false);

  const productSnapshot = item.product_snapshot;
  const isLoading = isUpdating || isRemoving;
  const lineTotal = item.price_minor_units * item.quantity;

  // Build link back to product configurator if we have the necessary IDs
  const productLink = item.generation_session_id && item.design_asset_id
    ? buildProductViewUrl(item.generation_session_id, item.design_asset_id)
    : null;

  // Close drawer when navigating to product
  const handleProductClick = useCallback((): void => {
    closeDrawer();
  }, [closeDrawer]);

  const handleRemove = useCallback((): void => {
    const productName = productSnapshot.product_type_name || 'Item';
    removeItem(item.id, {
      onSuccess: () => {
        toast.success(t('ui.cart.itemRemovedFromCart', { productName }));
      },
      onError: (error) => {
        toast.error(t('ui.cart.failedToRemoveItem'), {
          description: error.message,
        });
      },
    });
  }, [item.id, productSnapshot.product_type_name, removeItem, t]);

  const handleIncrement = useCallback((): void => {
    updateQuantity(
      { itemId: item.id, quantity: item.quantity + 1 },
      {
        onError: (error) => {
          toast.error(t('ui.cart.failedToUpdateQuantity'), {
            description: error.message,
          });
        },
      }
    );
  }, [item.id, item.quantity, updateQuantity, t]);

  const handleDecrement = useCallback((): void => {
    if (item.quantity <= 1) {
      handleRemove();
    } else {
      updateQuantity(
        { itemId: item.id, quantity: item.quantity - 1 },
        {
          onError: (error) => {
            toast.error(t('ui.cart.failedToUpdateQuantity'), {
              description: error.message,
            });
          },
        }
      );
    }
  }, [item.id, item.quantity, updateQuantity, handleRemove, t]);

  return (
    <MotionBox
      layout
      initial={itemAnimation.initial}
      animate={itemAnimation.animate}
      exit={itemAnimation.exit}
      transition={itemAnimation.transition}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative"
      style={{
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      <Stack gap={4} className="py-6">
        {/* Product Hero Section */}
        <Row gap={4} align="start">
          {/* Large Thumbnail - Product as Hero (Clickable if link available) */}
          <Box
            style={{
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {productLink ? (
              <Link to={productLink} onClick={handleProductClick} style={{ display: 'block' }}>
                <MotionBox
                  style={{
                    width: 'clamp(6rem, 6rem + 1vw, 7rem)',
                    height: 'clamp(6rem, 6rem + 1vw, 7rem)',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    backgroundColor: 'hsl(var(--muted) / 0.2)',
                    cursor: 'pointer',
                  }}
                  whileHover={thumbnailHover}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                  {productSnapshot.design_thumbnail_url ? (
                    <img
                      src={productSnapshot.design_thumbnail_url}
                      alt={productSnapshot.product_type_name || 'Product'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Center fullWidth fullHeight>
                      <Text size="heading-1" variant="muted">🎁</Text>
                    </Center>
                  )}

                  {/* Loading overlay */}
                  <AnimatePresence>
                    {isLoading && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <AbsoluteCenter
                          style={{
                            backgroundColor: 'hsl(var(--background) / 0.8)',
                          }}
                        >
                          <Icon icon={Loader2} size="md" color="primary" animate="spin" />
                        </AbsoluteCenter>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </MotionBox>
              </Link>
            ) : (
              <MotionBox
                style={{
                  width: 'clamp(6rem, 6rem + 1vw, 7rem)',
                  height: 'clamp(6rem, 6rem + 1vw, 7rem)',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  backgroundColor: 'hsl(var(--muted) / 0.2)',
                }}
                whileHover={thumbnailHover}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                {productSnapshot.design_thumbnail_url ? (
                  <img
                    src={productSnapshot.design_thumbnail_url}
                    alt={productSnapshot.product_type_name || 'Product'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Center fullWidth fullHeight>
                    <Text size="heading-1" variant="muted">🎁</Text>
                  </Center>
                )}

                {/* Loading overlay */}
                <AnimatePresence>
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <AbsoluteCenter
                        style={{
                          backgroundColor: 'hsl(var(--background) / 0.8)',
                        }}
                      >
                        <Icon icon={Loader2} size="md" color="primary" animate="spin" />
                      </AbsoluteCenter>
                    </motion.div>
                  )}
                </AnimatePresence>
              </MotionBox>
            )}
          </Box>

          {/* Product Details - Clean and Simple */}
          <Stack gap={2} className="flex-1 min-w-0">
            <Text weight="semibold" size="base" variant="default" className="line-clamp-2">
              {productSnapshot.product_type_name || t('ui.cart.customProduct')} {productSnapshot.size_dimensions}
            </Text>

            {productSnapshot.size_name && (
              <Text size="sm" variant="muted">
                {t('ui.cart.size')} {productSnapshot.size_name}
              </Text>
            )}

            {productSnapshot.design_name && (
              <Text size="xs" variant="muted" className="line-clamp-1">
                {productSnapshot.design_name}
              </Text>
            )}
          </Stack>

          {/* Price - Prominent */}
          <Stack align="end" gap={1}>
            <Text weight="bold" size="lg" variant="default">
              {formatPrice(lineTotal, item.currency)}
            </Text>
            {item.quantity > 1 && (
              <Text size="xs" variant="muted">
                {formatPrice(item.price_minor_units, item.currency)} {t('ui.cart.each')}
              </Text>
            )}
          </Stack>
        </Row>

        {/* Controls - Minimal, Elegant */}
        <Row justify="between" align="center">
          {/* Quantity Controls - Simplified */}
          <LiquidGlass
            thickness="thin"
            rounded="md"
            padding="none"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.375rem 0.5rem',
            }}
          >
            <Button
              asChild
              variant="ghost"
              size="icon"
              disabled={isLoading}
            >
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                onClick={handleDecrement}
                disabled={isLoading}
                aria-label="Decrease quantity"
                className="h-7 w-7"
              >
                <Icon icon={Minus} size="sm" />
              </motion.button>
            </Button>

            <Text
              weight="semibold"
              variant="default"
              align="center"
              style={{ width: '2rem' }}
            >
              {item.quantity}
            </Text>

            <Button
              asChild
              variant="ghost"
              size="icon"
              disabled={isLoading}
            >
              <motion.button
                whileHover={buttonHover}
                whileTap={buttonTap}
                onClick={handleIncrement}
                disabled={isLoading}
                aria-label="Increase quantity"
                className="h-7 w-7"
              >
                <Icon icon={Plus} size="sm" />
              </motion.button>
            </Button>
          </LiquidGlass>

          {/* Remove Button - Only visible on hover */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Button
                  asChild
                  variant="ghost-destructive"
                  size="sm"
                  onClick={handleRemove}
                  disabled={isLoading}
                >
                  <motion.button
                    whileHover={buttonHover}
                    whileTap={buttonTap}
                    aria-label="Remove item"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                  >
                    <Icon icon={Trash2} size="sm" />
                    <Text size="xs">{t('ui.cart.remove')}</Text>
                  </motion.button>
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </Row>
      </Stack>
    </MotionBox>
  );
});
